import { useState } from 'react';

import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Checkbox from '@mui/material/Checkbox';
import Typography from '@mui/material/Typography';
import LoadingButton from '@mui/lab/LoadingButton';

import { useTranslate } from 'src/locales';

import Iconify from 'src/components/iconify';

export interface PlaceholderSuggestions {
  placeholders: { paragraph: number; find: string; placeholder: string }[];
  notes: { from: number; to: number; advice: string }[];
  paragraphs: { index: number; text: string }[];
}

/** The paragraph with the suggested text marked, so the admin sees what would be replaced. */
function InContext({ text, find }: { text: string; find: string }) {
  const at = text.indexOf(find);
  if (at < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, at)}
      <Box component="mark" sx={{ bgcolor: 'warning.lighter', px: 0.25, borderRadius: 0.5 }}>{find}</Box>
      {text.slice(at + find.length)}
    </>
  );
}

/**
 * AI's suggestions for an untagged Word layout (JT1-1887, B11): each one shows the text in its
 * paragraph and the placeholder it would become. The admin picks which to apply.
 */
export default function PlaceholderSuggestionsPanel({ suggestions, applying, onApply }: { suggestions: PlaceholderSuggestions; applying: boolean; onApply: (chosen: PlaceholderSuggestions['placeholders']) => void }) {
  const { t } = useTranslate();
  const [chosen, setChosen] = useState<Set<number>>(() => new Set(suggestions.placeholders.map((_, i) => i)));
  const paragraph = (index: number) => suggestions.paragraphs.find((p) => p.index === index)?.text ?? '';
  const toggle = (i: number) => setChosen((current) => {
    const next = new Set(current);
    if (next.has(i)) next.delete(i);
    else next.add(i);
    return next;
  });

  if (!suggestions.placeholders.length && !suggestions.notes.length) {
    return <Alert severity="info">{t('presentations.word.ai_nothing')}</Alert>;
  }

  return (
    <Stack spacing={1.5}>
      {!!suggestions.placeholders.length && (
        <Box sx={{ maxHeight: 280, overflow: 'auto', border: (theme) => `1px solid ${theme.palette.divider}`, borderRadius: 1 }}>
          {suggestions.placeholders.map((s, i) => (
            <Stack key={`${s.paragraph}-${s.find}`} direction="row" spacing={1} alignItems="flex-start" sx={{ p: 1, '&:not(:last-of-type)': { borderBottom: (theme) => `1px dashed ${theme.palette.divider}` } }}>
              <Checkbox size="small" checked={chosen.has(i)} onChange={() => toggle(i)} inputProps={{ 'aria-label': t('presentations.word.ai_use', { text: s.find }) }} sx={{ p: 0.5 }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ wordBreak: 'break-word' }}><InContext text={paragraph(s.paragraph)} find={s.find} /></Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'monospace' }}>→ {`{${s.placeholder}}`}</Typography>
              </Box>
            </Stack>
          ))}
        </Box>
      )}

      {suggestions.notes.map((note) => (
        <Alert key={`${note.from}-${note.to}`} severity="info" icon={<Iconify icon="solar:repeat-bold" />}>
          <Typography variant="body2">{note.advice}</Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>{paragraph(note.from).slice(0, 80)}</Typography>
        </Alert>
      ))}

      {!!suggestions.placeholders.length && (
        <LoadingButton variant="outlined" loading={applying} disabled={!chosen.size} onClick={() => onApply(suggestions.placeholders.filter((_, i) => chosen.has(i)))} sx={{ alignSelf: 'flex-start' }}>
          {t('presentations.word.ai_apply', { count: chosen.size })}
        </LoadingButton>
      )}
    </Stack>
  );
}
